import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { fetchProfileFromAPI } from "./fetchProfileFromAPI";
import { config } from '../config';
import { isTokenExpired } from '../utils/jwtUtils';
import { getDeviceId } from '../utils/browserFingerprint';

type User = {
  id: string;
  login: string;
  name: string;
  phone: string;
  favorite_symbols: string[];
  account: {
    name: string;
    is_trading: boolean;
  };
};

type AuthResponse = {
  access_token: string;
  refresh_token: string;
  login: string;
  name: string;
  id: string;
};

type RefreshTokenResponse = {
  access_token: string;
  refresh_token: string;
  id: string;
  login: string;
};

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => false,
  logout: () => {},
  refreshToken: async () => false,
  isAuthenticated: false,
  isLoading: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Add refresh token state to prevent concurrent calls
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshPromiseRef = React.useRef<Promise<boolean> | null>(null);

  const fetchProfile = async (accessToken: string) => {
    try {
      const profile = await fetchProfileFromAPI(accessToken);
      
      // Ensure account property is properly structured with is_trading property
      const userProfile: User = {
        ...profile,
        account: {
          name: profile.account?.name || profile.name || "",
          is_trading: profile.account?.is_trading !== undefined ? profile.account.is_trading : profile.is_trading || false
        }
      };
      
      setUser(userProfile);
      localStorage.setItem("user", JSON.stringify(userProfile));
    } catch (error) {
      console.error("Profile fetch failed:", error);
      toast.error("Failed to load user profile");
      throw error;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    // If already refreshing, return the existing promise
    if (isRefreshing && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    setIsRefreshing(true);
    
    // Create a new refresh promise
    refreshPromiseRef.current = performTokenRefresh();
    
    try {
      const result = await refreshPromiseRef.current;
      return result;
    } finally {
      setIsRefreshing(false);
      refreshPromiseRef.current = null;
    }
  };

  const performTokenRefresh = async (): Promise<boolean> => {
    try {
      const refreshTokenValue = localStorage.getItem("refresh_token");
      
      if (!refreshTokenValue) {
        console.error("No refresh token available");
        return false;
      }

      console.log("Attempting to refresh token...");
      const response = await fetch(`${config.apiUrl}refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshTokenValue }),
      });

      if (!response.ok) {
        console.error("Token refresh failed with status:", response.status);
        throw new Error('Token refresh failed');
      }

      const data: RefreshTokenResponse = await response.json();
      
      // Store new tokens
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      
      // Update existing user data with preserved id and login
      const existingUser = localStorage.getItem("user");
      if (existingUser) {
        const userData = JSON.parse(existingUser);
        userData.id = data.id;
        userData.login = data.login;
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
      } else {
        // If no existing user data, fetch fresh profile
        await fetchProfile(data.access_token);
      }
      
      console.log("Token refreshed successfully with user info preserved");
      return true;
    } catch (error) {
      console.error("Token refresh error:", error);
      logout();
      return false;
    }
  };

  // Listen for token refresh failures from apiClient
  useEffect(() => {
    const handleTokenRefreshFailed = () => {
      console.log("Token refresh failed, logging out user");
      logout();
      toast.error("Session expired. Please log in again.");
    };

    window.addEventListener('tokenRefreshFailed', handleTokenRefreshFailed);
    
    return () => {
      window.removeEventListener('tokenRefreshFailed', handleTokenRefreshFailed);
    };
  }, []);

  // Check for existing tokens on initial load
  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem("access_token");
      const refreshTokenValue = localStorage.getItem("refresh_token");
      
      if (!accessToken || !refreshTokenValue) {
        setIsLoading(false);
        return;
      }

      try {
        // Check if access token is expired
        if (isTokenExpired(accessToken)) {
          console.log("Access token expired, attempting refresh...");
          const refreshSuccess = await refreshToken();
          if (refreshSuccess) {
            setIsAuthenticated(true);
          }
        } else {
          // Token is still valid, fetch profile
          console.log("Access token is valid, fetching profile...");
          await fetchProfile(accessToken);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Auth restoration failed:", error);
        // Only clear tokens if it's not a network error
        if (!error?.message?.includes('fetch')) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("user");
        }
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const deviceId = getDeviceId();
      console.log('Login attempt with device ID:', deviceId);
      
      const response = await fetch(`${config.apiUrl}login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username, 
          password,
          device_id: deviceId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();      
        const errorMessage = errorData?.error || "Login failed";
        console.error("Login failed with status:", response.status, "Message:", errorMessage);

        if (response.status === 401) {
          toast.error(errorMessage); // Display specific message from API
        } else {
          toast.error("Login failed. Please try again.");
        }

        return false;
      }

      const data: AuthResponse = await response.json();
      console.log("Login successful, received tokens");
      
      // Store tokens
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      
      // Fetch profile after successful login
      await fetchProfile(data.access_token);
      
      setIsAuthenticated(true);
      // Removed toast.success("Successfully logged in"); message
      return true;
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed. Please try again.");
      return false;
    }
  };

  const logout = async () => {
    try {
      const deviceId = getDeviceId();
      const accessToken = localStorage.getItem("access_token");
      
      console.log("Calling logout API with device ID:", deviceId);
      
      // Call logout API if we have an access token
      if (accessToken) {
        await fetch(`${config.apiUrl}logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ 
            device_id: deviceId
          }),
        });
      }
    } catch (error) {
      console.error("Logout API error:", error);
      // Continue with local logout even if API call fails
    } finally {
      console.log("Logging out, clearing authentication data");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      setUser(null);
      setIsAuthenticated(false);
      // Reset refresh state
      setIsRefreshing(false);
      refreshPromiseRef.current = null;
      // Removed toast.info("Logged out successfully"); message
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshToken, isAuthenticated, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
